import { inject, Injectable, computed } from '@angular/core';
import { dispatch, select } from '@ngxs/store';
import { nanoid } from 'nanoid';
import { firstValueFrom, filter, take, timeout } from 'rxjs';

import { NavigationService } from '../nav/nav.service';
import {
	CreateIntakeForm,
	UpdateStatus,
} from '../../state/matter/client-intake/client-intake.actions';
import { MatterClientIntakeFormState } from '../../state/matter/client-intake/client-intake.state';
import { PortalInviteState } from '../../state/portal-invite/portal-invite.state';
import { BusinessUsersState } from '../../state/business-users/business-users.state';
import { UserProfileState } from '../../state/user-profile/state';
import {
	$clientIntakePageViewModels,
	$intakeFormContextData,
	$intakeFormCreationData,
} from '../../../private/sale/parties/client-intake-page/view-models/selectors';

import {
	InviteAccessMethod,
	PortalInviteStatus,
	PortalServiceType,
	PortalInvite,
} from '@models/portal-invites';
import { CreateIntakeFormItems } from '@domain/intake-form-items';
import {
	AddRemoveClientContext,
	UpsertIntakeFormContext,
} from '@domain/intake-form-context';
import { IntakeFormStatus } from '@models/client-intake';

import { Store } from '@ngxs/store';
import { ModalService } from 'src/app/ui/modal/modal.service';
import { CancelIntakeFormConfirmationModalComponent } from '../../../private/sale/parties/client-intake-page/components/cancel-intake-form-confirmation-modal/cancel-intake-form-confirmation-modal.component';
import { UI_TYPES } from 'src/app/ui/modal/modal.types';
import { SelectedMatter } from '@state/matter/matter.selectors';
import { UpdatePortalInvite } from '../../state/portal-invite/portal-invite.actions';
import { IEmailRecipient } from '../email/email.service.types';
import { SnackbarService } from '@services/ui/snackbar.service';
import { DrawerService } from 'src/app/ui/drawer/drawer.service';
import {
	EmailTemplatesDrawerComponent,
	EmailTemplatesDrawerData,
	EmailTemplatesDrawerResult,
} from '../../components/email-templates-drawer/email-templates-drawer.component';
import {
	ClientSubType,
	EmailRecipientType,
} from '@models/global-email-templates';
import { MatterClients } from '../../state/global/matter_clients';
import { ConfirmEmailSentModalComponent } from '../../../private/sale/parties/client-intake-page/components/confirm-email-sent-modal/confirm-email-sent-modal.component';
import { BusinessFeatureFlagSelectors } from '../../state/business/business-feature-flag.selectors';
import { EmailTemplatesSelectors } from '../../state/email-templates/email-templates.selectors';
import { ShareIntakeDrawerComponent } from '../../../private/sale/parties/client-intake-page/components/share-intake-drawer/share-intake-drawer.component';
import { SelectEmailTemplate } from '@state/email-templates/email-templates.actions';
import { EmailIntegrationApiService } from '../../api-repos/email-integration/email-integration.api.service';
import { ContextAccessors } from '@models/matter';
import { CreatePortalInvite } from '@state/portal-invite/portal-invite.actions';
import { SyncPortalEngineContextAndResponses } from '@domain/portal-engine';

@Injectable({
	providedIn: 'root',
})
export class ClientIntakeService {
	navService = inject(NavigationService);
	private modalService = inject(ModalService);
	private snackbarService = inject(SnackbarService);
	private drawerService = inject(DrawerService);
	private store = inject(Store);
	private emailApiService = inject(EmailIntegrationApiService);

	private activeIntakeForm_ = select(
		MatterClientIntakeFormState.getMostRecentActiveForm,
	);
	private intakeFormCreationData_ = select($intakeFormCreationData);
	private intakeFormContextData_ = select($intakeFormContextData);
	private clientIntakePage_ = select($clientIntakePageViewModels);
	private loggedInUser_ = select(BusinessUsersState.getLoggedInBusinessUser);
	private businessId_ = select(UserProfileState.getCurrentBusinessId);
	private invitees_ = select(PortalInviteState.createInviteesFromMatterClients);
	private matterId_ = select(SelectedMatter.id);
	private intakeFormContent_ = select(SelectedMatter.getIntakeFormContent);
	private getInviteByPortalServiceId_ = select(
		PortalInviteState.getInviteByPortalServiceId,
	);
	private matter_ = select(SelectedMatter.getSelected);
	private clients_ = select(
		MatterClients.get_matterClientsWithAssociationMapped,
	);
	private outlookIntegrationEnabled_ = select(
		BusinessFeatureFlagSelectors.isOutlookIntegrationEnabled,
	);
	private emailTemplateOptionsByRecipient_ = select(
		EmailTemplatesSelectors.$emailTemplateOptionsByRecipient,
	);
	private useEmailIntegrationForIntake_ = select(
		BusinessFeatureFlagSelectors.isOutlookIntegrationEnabledForClientIntake,
	);

	private selectedEmailTemplate$ = this.store.select(
		EmailTemplatesSelectors.$selectedEmailTemplate,
	);

	private createIntakeForm = dispatch(CreateIntakeForm);
	private createIntakeFormItems = dispatch(CreateIntakeFormItems);
	private upsertIntakeFormContext = dispatch(UpsertIntakeFormContext);
	private updateIntakeFormStatus = dispatch(UpdateStatus);
	private updatePortalInvite = dispatch(UpdatePortalInvite);
	private setSelectedTemplate = dispatch(SelectEmailTemplate);
	private addRemoveClientContext = dispatch(AddRemoveClientContext);
	private createPortalInvite = dispatch(CreatePortalInvite);
	private syncPortalEngineContextAndResponses = dispatch(
		SyncPortalEngineContextAndResponses,
	);

	private currPortalInvite_ = computed(() => {
		const intakeForm = this.activeIntakeForm_();
		if (!intakeForm?.id) {
			return null;
		}
		const getInviteByIntakeFormId = this.getInviteByPortalServiceId_();
		return getInviteByIntakeFormId(intakeForm.id);
	});

	getIntakeButtonText(): string {
		const portalInviteStatus =
			this.clientIntakePage_()?.$intakePageViewModel?.portalInvite?.status;

		return portalInviteStatus === PortalInviteStatus.ACTIVE
			? 'View Intake'
			: 'Send Request';
	}

	hasActiveIntakeForm(): boolean {
		return this.activeIntakeForm_() !== null;
	}

	async sendOrViewIntake(matterId: string): Promise<void> {
		const currIntakeForm = this.activeIntakeForm_();
		const inProgressStatuses = [
			IntakeFormStatus.WAITING_FOR_UPDATES,
			IntakeFormStatus.REVIEW_UPDATES,
			IntakeFormStatus.COMPLETE,
		];

		if (currIntakeForm) {
			if (currIntakeForm.status === IntakeFormStatus.DRAFT) {
				this.navService.goToMatter_ClientIntake_PreviewMode(matterId);
			} else if (inProgressStatuses.includes(currIntakeForm.status)) {
				this.navService.goToMatter_ClientIntake_Review(matterId);
			}

			return;
		}

		const intakeFormId = nanoid(36);
		await this.generatePortalInvite(matterId, intakeFormId);
		await this.createIntakeFormData(intakeFormId);
		this.navService.goToMatter_ClientIntake_PreviewMode(matterId);
	}

	private async createIntakeFormData(intakeFormId: string): Promise<string> {
		// TODO: convert this to a function
		const clientIntakeForm = {
			...this.intakeFormCreationData_(),
			id: intakeFormId,
		};

		// Wait for form document to be created before creating subcollection items
		await firstValueFrom(
			this.createIntakeForm({
				clientIntakeForm,
			}),
		);

		await firstValueFrom(this.createIntakeFormItems(this.intakeFormContent_()));

		await firstValueFrom(
			this.upsertIntakeFormContext({
				...this.intakeFormContextData_(),
			}),
		);

		await firstValueFrom(
			this.syncPortalEngineContextAndResponses({
				clients: this.clients_(),
				matter: this.matter_(),
				loggedInUser: this.loggedInUser_(),
			}),
		);

		return intakeFormId;
	}

	private async generatePortalInvite(
		matterId: string,
		intakeFormId: string,
		expiresAt?: number,
	): Promise<void> {
		const now = Date.now();
		const loggedInUser = this.loggedInUser_();
		const businessId = this.businessId_();
		const invitees = this.invitees_();

		if (!loggedInUser || !businessId) {
			throw new Error('Missing required user or business information');
		}

		await firstValueFrom(
			this.createPortalInvite({
				portalInvite: {
					id: nanoid(36),
					portalService: PortalServiceType.CLIENT_PORTAL,
					portalServiceId: intakeFormId,
					matterId,
					businessId,
					invitees,
					createdAt: now,
					accessMethod: InviteAccessMethod.EMAIL,
					expiresAt: typeof expiresAt === 'number' ? expiresAt : null, // Expiry can be defined by the user in the future, using null for now
					createdBy: loggedInUser.id,
					updatedBy: loggedInUser.id,
					updatedAt: now,
					status: PortalInviteStatus.DRAFT,
					statusHistory: [
						{
							status: PortalInviteStatus.DRAFT,
							updatedBy: loggedInUser.id,
							updatedAt: now,
						},
					],
				},
			}),
		);
	}

	setIntakeFormStatus(intakeFormStatus: IntakeFormStatus) {
		const intakeForm = this.activeIntakeForm_();

		if (!intakeForm) {
			console.warn('Cannot set intake form status, no intake form found');
			return;
		}

		if (intakeFormStatus === IntakeFormStatus.CANCELLED) {
			this.getCancellationConfirmation();
			return;
		}

		this.updateIntakeFormStatus({
			intakeFormId: intakeForm.id,
			status: intakeFormStatus,
		});
	}

	private getCancellationConfirmation() {
		return this.modalService.openModal(
			CancelIntakeFormConfirmationModalComponent,
			{
				title: 'Cancel Intake Request',
				size: 'sm',
				type: UI_TYPES.DANGER,
				primaryAction: {
					text: 'Continue',
					action: () => {
						this.modalService.closeModal();
						this.cancelCurrentIntakeForm();
					},
				},
				secondaryAction: {
					text: 'Back',
					action: () => {
						this.modalService.closeModal();
					},
				},
			},
		);
	}

	private cancelCurrentIntakeForm() {
		const intakeForm = this.activeIntakeForm_();

		if (!intakeForm) {
			console.warn('Cannot cancel intake form, no intake form found');
			return;
		}

		this.updateIntakeFormStatus({
			intakeFormId: intakeForm.id,
			status: IntakeFormStatus.CANCELLED,
		});

		const matterId = this.matterId_();
		if (matterId) {
			this.navService.goToMatter_Parties(matterId);
		}
	}

	handleEmailSent(data: {
		allEmailRecipients: string[];
		successSnackbarMsg?: string;
	}): void {
		const { allEmailRecipients, successSnackbarMsg } = data;
		const intakeForm = this.activeIntakeForm_();
		const portalInvite = this.currPortalInvite_();

		if (!intakeForm || !portalInvite) {
			console.error(
				'Cannot handle email sent, missing intake form or portal invite',
			);
			return;
		}

		this.addRemoveClientContext({
			currentContext: this.intakeFormContextData_(),
			clients: this.clients_(),
		});

		const updatedPortalInvite = this.getPortalInviteUpdate(
			portalInvite,
			allEmailRecipients.map(
				(email) => ({ email, displayName: '' }) as IEmailRecipient,
			),
			PortalInviteStatus.ACTIVE,
		);

		this.updatePortalInvite({
			inviteId: portalInvite.id,
			portalInvite: updatedPortalInvite,
			updatedBy: this.loggedInUser_().id,
		});

		this.updateIntakeFormStatus({
			intakeFormId: intakeForm.id,
			status: IntakeFormStatus.WAITING_FOR_UPDATES,
		});

		if (successSnackbarMsg) {
			this.snackbarService.openSnackBar({
				text: successSnackbarMsg,
				type: 'success',
			});
		}

		this.navService.goToMatter_Parties(this.matterId_());
	}

	async sendIntakeEmail() {
		const validation = this.canSendIntakeEmail();
		if (!validation.canSend) {
			this.snackbarService.openSnackBar({
				text: validation?.errorMessage,
				type: 'error',
			});
			return;
		}

		const intakeForm = this.activeIntakeForm_();
		const portalInvite = this.currPortalInvite_();

		if (!intakeForm || !portalInvite) {
			console.error(
				'Cannot send intake email, missing intake form or portal invite',
			);
			return;
		}

		// Currently the Outlook integration flow is primarily triggered when the isOutlookIntegrationEnabled feature flag is turned on
		// In the future, a check will be added to see if the user has connected their Outlook account as well [Issue #1762797942139]
		if (
			this.useEmailIntegrationForIntake_() &&
			this.outlookIntegrationEnabled_()
		) {
			// Handle email sending flow with Outlook integration by opening the email templates drawer
			this.drawerService
				.openDrawer(EmailTemplatesDrawerComponent, {
					title: 'Send Client Intake Form',
					data: {
						to: portalInvite.invitees.map((i) => i.email),
						templateOptions: this.emailTemplateOptionsByRecipient_()(
							EmailRecipientType.CLIENT,
							ClientSubType.ALL,
						),
						context: {
							clientIntakes: [{ referenceId: intakeForm.id }], //Forcing the context to have clientIntakes object to replace all the `clientIntakes.id.something` tags. Not an official intake atm.
						},
						ignoreEmailIntegration: !this.useEmailIntegrationForIntake_(),
						additionalTags: {
							clientIntakes: {
								[intakeForm.id]: {
									portalInviteId: portalInvite.id,
								},
							},
						},
					} as EmailTemplatesDrawerData,
				})
				.afterClosed()
				.pipe(take(1))
				.subscribe((result: EmailTemplatesDrawerResult) => {
					if (!result.sentSuccessfully) {
						return;
					}

					const allEmailRecipients = [
						...result.to,
						...result.cc,
						...result.bcc,
					];
					const emailRecipientCount = allEmailRecipients.length;
					const successSnackbarMsg = `Email sent to ${emailRecipientCount} contacts`;

					this.handleEmailSent({
						allEmailRecipients,
						successSnackbarMsg,
					});
				});
		} else {
			// Handle manual email sending flow by showing modal for user to confirm that email has
			// been sent before updating intake form status and invite status
			const allEmailRecipients = portalInvite.invitees.map((i) => i.email);

			const emailTemplateOptions = this.emailTemplateOptionsByRecipient_()(
				EmailRecipientType.CLIENT,
				ClientSubType.ALL,
			);

			if (emailTemplateOptions.length === 0) {
				console.error('No email templates available for client intake form.');
				return;
			}

			const templateOptionId = emailTemplateOptions[0].id;
			await firstValueFrom(this.setSelectedTemplate(templateOptionId));

			const selectedEmailTemplate = await firstValueFrom(
				this.selectedEmailTemplate$.pipe(
					filter((t) => !!t.version?.htmlMessage),
					take(1),
					timeout(5000),
				),
			);

			const convertedTemplate = await firstValueFrom(
				this.emailApiService.convertEmailTemplate(
					this.matterId_(),
					this.businessId_(),
					selectedEmailTemplate?.version?.htmlMessage || '',
					{
						[ContextAccessors.CLIENT_INTAKES]: [{ referenceId: intakeForm.id }],
					},
					{
						clientIntakes: {
							[intakeForm.id]: {
								portalInviteId: portalInvite.id,
							},
						},
					},
				),
			);

			const convertedEmailSubject = await firstValueFrom(
				this.emailApiService.convertEmailTemplate(
					this.matterId_(),
					this.businessId_(),
					selectedEmailTemplate.version.subject,
				),
			);

			const formattedEmailBody = this.formatEmailBody(convertedTemplate);

			const mailtoLink = `mailto:${allEmailRecipients.join(',')}?subject=${encodeURIComponent(convertedEmailSubject)}&body=${formattedEmailBody}`;
			window.location.href = mailtoLink;

			this.modalService.openModal(ConfirmEmailSentModalComponent, {
				title: 'Update Intake Status',
				size: 'sm',
				type: UI_TYPES.DEFAULT,
				primaryAction: {
					text: 'Request Sent',
					action: () => {
						this.handleEmailSent({
							allEmailRecipients,
							successSnackbarMsg: 'Client intake request complete',
						});

						this.syncPortalEngineContextAndResponses({
							clients: this.clients_(),
							matter: this.matter_(),
							loggedInUser: this.loggedInUser_(),
						});

						this.modalService.closeModal();
					},
				},
				secondaryAction: {
					text: 'Back',
					action: () => {
						this.modalService.closeModal();
					},
				},
			});
		}
	}

	private canSendIntakeEmail(): { canSend: boolean; errorMessage?: string } {
		const portalInvite = this.currPortalInvite_();

		if (!portalInvite?.invitees) {
			return {
				canSend: false,
				errorMessage: 'Please add at least one client to the access list.',
			};
		}

		const inviteesWithoutEmail =
			portalInvite.invitees.filter(
				(invitee) => !invitee.email || invitee.email.trim() === '',
			) ?? [];

		if (inviteesWithoutEmail.length > 0) {
			return {
				canSend: false,
				errorMessage:
					'All clients must have email addresses before sending the intake form.',
			};
		}

		return { canSend: true };
	}

	private getPortalInviteUpdate(
		portalInvite: PortalInvite,
		emailRecipients: IEmailRecipient[],
		newInviteStatus: PortalInviteStatus,
	): Partial<PortalInvite> {
		const loggedInUserId = this.loggedInUser_().id;

		const emailSet = new Set(emailRecipients.map((r) => r.email.toLowerCase()));
		const now = Date.now();

		const updatedPortalInvitees = portalInvite.invitees.map((invitee) => {
			if (emailSet.has(invitee.email.toLowerCase())) {
				const updatedHistory = [
					...invitee.sentHistory,
					{ sentAt: now, sentBy: loggedInUserId },
				];

				return {
					...invitee,
					sentHistory: updatedHistory,
				};
			}

			return invitee;
		});

		const currInviteStatus = portalInvite.status;
		const updatedStatusHistory =
			currInviteStatus !== newInviteStatus
				? [
						...portalInvite.statusHistory,
						{
							status: newInviteStatus,
							updatedBy: loggedInUserId,
							updatedAt: now,
						},
					]
				: portalInvite.statusHistory;

		const portalInviteUpdate = {
			invitees: updatedPortalInvitees,
			status: newInviteStatus,
			statusHistory: updatedStatusHistory,
		};

		return portalInviteUpdate;
	}

	shareIntakeForm() {
		this.drawerService
			.openDrawer(ShareIntakeDrawerComponent, {
				title: 'Share',
			})
			.afterClosed()
			.pipe(take(1))
			.subscribe();
	}

	private formatEmailBody(
		html: string,
		tags = ['</p>', '</table>', '<br/>', '</li>', '<br>'],
	): string {
		// Use DOMParser to parse HTML into actual DOM elements
		const parser = new DOMParser();
		const doc = parser.parseFromString(html, 'text/html');

		// Extract and format all anchor tags
		const anchors = doc.querySelectorAll('a');
		anchors.forEach((anchor) => {
			const url = anchor.href;
			const text = anchor.textContent?.trim() || '';

			// Create replacement text node
			let replacementText: string;
			if (text === url || text === '') {
				replacementText = url;
			} else {
				replacementText = `${text} (${url})`;
			}

			const textNode = doc.createTextNode(replacementText);
			anchor.replaceWith(textNode);
		});

		html = doc.body.innerHTML;

		tags.forEach((tag) => {
			html = html.replace(new RegExp(tag, 'g'), `${tag}\n`);
		});

		html = html.replace(/&nbsp;/g, ' ');

		// Strip all HTML tags
		html = html.replace(/(<([^>]+)>)|(&lt;([^>]+)&gt;)/g, '');

		// Normalize whitespace: replace tabs, multiple spaces, and multiple newlines
		html = html.replace(/\t+/g, ' '); // Replace tabs with single space
		html = html.replace(/ {2,}/g, ' '); // Replace multiple spaces with single space
		html = html.replace(/(\n\s*){3,}/g, '\n\n'); // Replace 3+ newlines (with optional spaces between them) with double newline
		html = html.trim();

		return encodeURIComponent(html);
	}
}
